import { AlertTriangle, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui/Button'
import { cn } from '@/lib/utils'

interface ChannelEditorDirtyBarProps {
  isDirty: boolean
  isSaving: boolean
  changeCount: number
  onSave: () => void
  onDiscard: () => void
  /** Optional: renders a secondary "Speichern & zum Produkt" button. Only shown when provided. */
  onSaveAndOpenProduct?: () => void
  /**
   * When true, render as a viewport-fixed slide-up bar (mirrors the Assets tab's
   * ProductImagesBottomBar) so it stays visible regardless of scroll. Default is
   * the in-flow sticky bar used inside overlays (Add-to-Product, RemakeApply).
   */
  floating?: boolean
}

export function ChannelEditorDirtyBar(props: ChannelEditorDirtyBarProps) {
  return props.floating ? <FloatingDirtyBar {...props} /> : <StickyDirtyBar {...props} />
}

/** In-flow sticky bar — original behaviour, used inside scrollable overlays. */
function StickyDirtyBar({ isDirty, isSaving, changeCount, onSave, onDiscard, onSaveAndOpenProduct }: ChannelEditorDirtyBarProps) {
  const { t } = useTranslation()
  if (!isDirty) return null

  return (
    <div
      role="region"
      aria-label={t('productContent.channels.dirty.unsavedCount', { count: changeCount })}
      className="sticky bottom-0 inset-x-0 z-20 flex items-center justify-between gap-3 px-4 py-2.5 border-t border-amber-300 dark:border-amber-700 bg-amber-50/95 dark:bg-amber-950/70 backdrop-blur"
    >
      <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-100">
        <span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden />
        <span>{t('productContent.channels.dirty.unsavedCount', { count: changeCount })}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onDiscard} disabled={isSaving}>
          {t('productContent.channels.dirty.discard')}
        </Button>
        {onSaveAndOpenProduct && (
          <Button variant="outline" size="sm" onClick={onSaveAndOpenProduct} disabled={isSaving}>
            {t('addToProduct.saveAndOpenProduct')}
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={onSave} loading={isSaving}>
          {isSaving ? t('productContent.channels.dirty.saving') : t('productContent.channels.dirty.save')}
        </Button>
      </div>
    </div>
  )
}

/**
 * Viewport-fixed slide-up bar — same behaviour as the Assets tab. Always mounted
 * and translated off-screen when clean, so the transition plays both ways and the
 * bar is visible at the bottom of the screen regardless of editor scroll position.
 */
function FloatingDirtyBar({ isDirty, isSaving, changeCount, onSave, onDiscard, onSaveAndOpenProduct }: ChannelEditorDirtyBarProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="channel-dirty-bar"
      role="region"
      aria-label={t('productContent.channels.dirty.unsavedCount', { count: changeCount })}
      aria-hidden={!isDirty}
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'transform transition-transform duration-300 ease-out',
        isDirty ? 'translate-y-0' : 'translate-y-full pointer-events-none',
      )}
    >
      <div className="bg-neutral-900 dark:bg-neutral-800 border-t border-neutral-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/20 shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-white font-medium">{t('common.unsavedChanges')}</p>
              <p className="text-neutral-400 text-sm">
                {t('productContent.channels.dirty.unsavedCount', { count: changeCount })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={onDiscard}
              disabled={isSaving}
              className="text-neutral-300 hover:text-white hover:bg-neutral-700"
            >
              {t('productContent.channels.dirty.discard')}
            </Button>
            {onSaveAndOpenProduct && (
              <Button
                variant="secondary"
                onClick={onSaveAndOpenProduct}
                disabled={isSaving}
              >
                {t('addToProduct.saveAndOpenProduct')}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={onSave}
              loading={isSaving}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
            >
              {isSaving ? (
                t('productContent.channels.dirty.saving')
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('productContent.channels.dirty.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
